import { useEffect, useRef } from "react";

/* `render=explicit` nu e opțional. Fără el, api.js randează singur, la încărcare,
   orice element cu clasa `cf-turnstile` — iar noi chemam și `render()` explicit pe
   același container. Ieșea o cursă între cele două, cu „Turnstile skipped implicit
   render because a widget already exists in this container" în consolă. */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface RenderOpts {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback"?: (code: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  /** always = mereu vizibil, execute = la execute(), interaction-only = doar când
   *  Cloudflare are nevoie de un om. */
  appearance?: "always" | "execute" | "interaction-only";
}

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, opts: RenderOpts) => string | undefined;
      remove: (id: string) => void;
      reset: (id?: string) => void;
      getResponse: (id?: string) => string | undefined;
    };
  }
}

interface Props {
  onToken: (token: string) => void;
  /** Chemat când verificarea nu poate rula: script blocat, widget respins,
   *  provocare expirată. Codul e cel dat de Cloudflare, ex. „600010". */
  onError?: (code: string) => void;
}

/**
 * Widgetul Cloudflare Turnstile.
 *
 * Nu randează nimic dacă VITE_TURNSTILE_SITE_KEY lipsește (dev / neconfigurat),
 * fiindcă verifyTurnstile de pe server e la fel un no-op în acel caz — deci
 * formularul rămâne funcțional local.
 *
 * Două lucruri învățate pe teren:
 *
 * 1. Codul cerea `size: "invisible"`, care nu e o valoare validă pentru `size`
 *    (normal | flexible | compact). Mai rău, un widget invizibil nu are unde să
 *    arate o provocare interactivă, iar widgetul nostru e configurat „Managed",
 *    adică Cloudflare decide singur dacă e nevoie de un om. Rezultatul ar fi fost
 *    un blocaj fără ieșire. `appearance: "interaction-only"` dă exact ce trebuia:
 *    invizibil în cazul obișnuit, vizibil doar când chiar se cere interacțiune.
 *
 * 2. Erorile nu erau prinse nicăieri. Când provocarea eșua, tokenul pur și simplu
 *    nu venea, iar vizitatorul afla abia după ce apăsa „Send brief" și primea 403
 *    de la server. Acum eșecul urcă imediat prin onError, iar formularul poate
 *    oferi o cale alternativă.
 */
const Turnstile = ({ onToken, onError }: Props) => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Callback-urile stau în ref-uri ca să nu intre în lista de dependențe: o
  // funcție nouă la fiecare randare ar demonta și reconstrui widgetul.
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    const ensureScript = (): Promise<void> =>
      new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${SCRIPT_SRC}"]`,
        );
        if (existing) {
          if (window.turnstile) resolve();
          else {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("script")), { once: true });
          }
          return;
        }
        const script = document.createElement("script");
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        // Un blocator de reclame sau o politică de rețea poate opri scriptul.
        // Fără ramura asta, formularul aștepta un token care nu venea niciodată.
        script.onerror = () => reject(new Error("script"));
        document.head.appendChild(script);
      });

    let cancelled = false;
    let retries = 0;
    const retry = (reason: string) => {
      if (cancelled) return;
      if (retries >= 2 || !widgetIdRef.current || !window.turnstile) {
        onErrorRef.current?.(reason);
        return;
      }
      retries += 1;
      window.turnstile.reset(widgetIdRef.current);
    };

    ensureScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        if (widgetIdRef.current) return;
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: "dark",
          appearance: "interaction-only",
          callback: (token) => onTokenRef.current(token),
          "error-callback": (code) => onErrorRef.current?.(String(code || "unknown")),
          // Expirarea și timeout-ul sunt recuperabile: un token nou se obține cu
          // reset(), fără să deranjăm vizitatorul. Se raportează abia dacă nici
          // reîncercările nu ajută, altfel s-ar putea învârti la infinit.
          "expired-callback": () => retry("expired"),
          "timeout-callback": () => retry("timeout"),
        });
        widgetIdRef.current = id ?? null;
        if (!id) onErrorRef.current?.("render-failed");
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.("script-blocked");
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} className="turnstile" />;
};

export default Turnstile;
